import axios from './instance'
import { getErrorMessage } from '../utils/helpers'

/**
 * Refund a selection of licenses and rental devices in one action.
 *
 * The API splits the selection by the purchase each item was bought on, because a
 * gateway credit references exactly one original transaction — so a selection spanning
 * two orders comes back as two refunds. Each is credited on its own, which is why the
 * response reports successes and refusals separately rather than failing whole: money
 * that has already left the gateway can't be taken back because a later credit was
 * refused.
 * @param {string} eventId
 * @param {string} eventExhibitorId
 * @param {object} selection `{ licenseIds, rentalDeviceIds, internalNote }`
 * @param {func} setError
 * @param {func} setLoading
 * @returns `{ refunds, failed }`, or null when nothing was credited
 */
export const issueRefund = async (
  eventId,
  eventExhibitorId,
  selection,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(
      `/events/${eventId}/exhibitors/${eventExhibitorId}/refunds/`,
      selection,
    )

    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * An exhibitor's rented devices, which are refunded alongside licenses and cancelled
 * the same way.
 * @param {string} eventId
 * @param {string} eventExhibitorId
 * @param {func} setError
 * @param {func} setLoading
 * @returns `data` results
 */
export const getRentalDevices = async (
  eventId,
  eventExhibitorId,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(
      `/events/${eventId}/exhibitors/${eventExhibitorId}/rental_devices/`,
    )

    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Withdraw a rented device, without touching whether it was paid for or refunded.
 * @param {string} eventId
 * @param {string} eventExhibitorId
 * @param {string} rentalDeviceId
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const cancelRentalDevice = async (
  eventId,
  eventExhibitorId,
  rentalDeviceId,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(
      `/events/${eventId}/exhibitors/${eventExhibitorId}/rental_devices/${rentalDeviceId}/cancel/`,
    )

    setLoading(false)
    setSuccess('The rental device has been cancelled.')
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}
